import PropTypes from "prop-types";
import React, { Component, PureComponent } from "react";
import { List } from "material-ui/List";
import CircularProgress from "material-ui/CircularProgress";
import shallowequal from "shallowequal";
import InfiniteMixin from "components/utils/infinite-mixin";
import { LayoutManager } from "components/buzz-lightyear";
import { css } from "glamor";
import { rawTheme as theme } from "../../styles/theme";

const styles = {
    loading: {
        textAlign: "center",
        padding: "24px 0",
    },
    hrStyle: {
        position: "relative",
        width: "100%",
        border: `0.9px solid ${theme.palette.grey}`,
    },
    unclickableTextStyle: {
        position: "relative",
        padding: "16px",
        width: "100%",
        color: "#000000",
        fontWeight: "bold",
        fontSize: "14px",
    },
    emptyComponentStyle: {
        padding: "110px 16px",
        textAlign: "center",
    },
};

class EmptyCollection extends PureComponent {
    static propTypes = {
        emptyCollectionComponent: PropTypes.element.isRequired,
    };

    render() {
        return <div {...css(styles.emptyComponentStyle)}>{this.props.emptyCollectionComponent}</div>;
    }
}

class Divider extends PureComponent {
    render() {
        return (
            <div style={{ padding: "8px 16px" }}>
                <hr {...css(styles.hrStyle)} />
            </div>
        );
    }
}

class UnclickableText extends React.Component {
    static propTypes = {
        displayText: PropTypes.string.isRequired,
    };

    render() {
        return (
            <div>
                <p {...css(styles.unclickableTextStyle)}>{this.props.displayText}</p>
            </div>
        );
    }
}

@InfiniteMixin({ watchViewAs: true })
class InfiniteListView extends Component {
    static propTypes = {
        searchTerm: PropTypes.string.isRequired,
        componentType: PropTypes.string.isRequired,
        childComponent: PropTypes.func.isRequired,
        childHeight: PropTypes.number.isRequired,
        height: PropTypes.number.isRequired,
        emptyResultsComponent: PropTypes.node.isRequired,
        collection: PropTypes.object.isRequired,
        onAction: PropTypes.func,
        selected: PropTypes.string,
        childComponentProps: PropTypes.object,
        preDisplayedComponents: PropTypes.object,
        shouldShowPreDisplayedComponents: PropTypes.bool,
        preDisplayedUnclickableComponentHeading: PropTypes.string,
        preDisplayedComponentRows: PropTypes.array,
        shouldDisplayDividingSeparator: PropTypes.bool,
        collectionsHeading: PropTypes.string,
        searchField: PropTypes.string,
    };

    scrollContainer = {};

    constructor(props) {
        super(props);

        const { childComponent, childHeight, collection } = props;

        this.layoutManager = new LayoutManager({
            componentTypes: {
                company: {
                    Component: childComponent,
                    height: childHeight,
                },
                unclickable: {
                    Component: UnclickableText,
                    height: 40,
                },
                hrLine: {
                    Component: Divider,
                    height: 20,
                },
                emptyCollection: {
                    Component: EmptyCollection,
                    height: 50,
                },
            },
        });

        this.collection = collection;
    }

    shouldComponentUpdate(nextProps, nextState, nextContext) {
        return (
            !shallowequal(this.props, nextProps) ||
            !shallowequal(this.state, nextState) ||
            !shallowequal(this.context, nextContext)
        );
    }

    componentDidMount() {
        if (!this.collection.length) {
            this.fetchNextPage(true, this.props);
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const currSearchTerm = this.props.searchTerm;
        const nextSearchTerm = nextProps.searchTerm;
        if (
            nextSearchTerm !== currSearchTerm ||
            (nextProps.scope.brand &&
                this.props.scope.brand &&
                nextProps.scope.brand._id !== this.props.scope.brand._id)
        ) {
            this.fetchNextPage(true, nextProps);
        }
    }

    handleFetchSuccess(collection, fetchedData) {
        const {
            componentType,
            selectedCompany,
            childComponentProps,
            pageSize,
            shouldShowPreDisplayedComponents,
            preDisplayedUnclickableComponentHeading,
            preDisplayedComponentRows,
            shouldDisplayDividingSeparator,
            collectionsHeading,
            emptyCollectionComponent,
            b2bPhase1,
            searchTerm,
        } = this.props;

        if (b2bPhase1 && this.collection.length <= pageSize) {
            if (shouldShowPreDisplayedComponents && preDisplayedComponentRows && preDisplayedComponentRows.length > 0) {
                if (
                    preDisplayedUnclickableComponentHeading !== null &&
                    preDisplayedUnclickableComponentHeading !== undefined
                ) {
                    this.layoutManager.addComponent(
                        {
                            displayText: preDisplayedUnclickableComponentHeading,
                        },
                        "unclickable",
                    );
                }

                if (preDisplayedComponentRows && preDisplayedComponentRows.length > 0) {
                    preDisplayedComponentRows.forEach((modelElement) => {
                        this.layoutManager.addComponent(
                            {
                                model: modelElement,
                                onAction: this.props.onAction,
                                isSelected: selectedCompany === modelElement._id,
                            },
                            componentType,
                        );
                    });
                }

                if (shouldDisplayDividingSeparator === true) {
                    this.layoutManager.addComponent(null, "hrLine");
                }
            }
            if (collectionsHeading && this.collection.length > 0 && !searchTerm) {
                this.layoutManager.addComponent({ displayText: collectionsHeading }, "unclickable");
            }
        }

        if (this.collection.length > 0) {
            this.collection.models.slice(this.collection.length - fetchedData.length).forEach((modelElement) => {
                this.layoutManager.addComponent(
                    {
                        model: modelElement,
                        onAction: this.props.onAction,
                        isSelected: selectedCompany === modelElement._id,
                        childComponentProps,
                    },
                    componentType,
                );
            });
        } else {
            if (b2bPhase1 && emptyCollectionComponent) {
                this.layoutManager.addComponent(
                    { emptyCollectionComponent: emptyCollectionComponent },
                    "emptyCollection",
                );
            }
        }
    }

    render() {
        const { emptyResultsComponent, height } = this.props;

        this.scrollContainer = this.getScrollContainer({
            loadingHeight: 80,
            height: height,
            loadingSpinner: (
                <div {...css(styles.loading)}>
                    <CircularProgress mode="indeterminate" data-test="list-circular-progress" />
                </div>
            ),
            emptyResults: emptyResultsComponent,
        });

        return <List>{this.scrollContainer}</List>;
    }
}

export default InfiniteListView;
